import { useSyncExternalStore } from 'react';

const MOBILE_BREAKPOINT = 768;

const INPUT_TAGS = new Set(['INPUT', 'TEXTAREA', 'SELECT']);

// ── Platform detection (stable after first client render) ──────────────
function detectPlatform() {
  if (typeof navigator === 'undefined') return { isIOS: false, isAndroid: false };
  const ua = navigator.userAgent;
  const isIOS =
    /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
  const isAndroid = /Android/.test(ua);
  return { isIOS, isAndroid };
}

let cachedPlatform: { isIOS: boolean; isAndroid: boolean } | null = null;
function getPlatform() {
  if (!cachedPlatform) cachedPlatform = detectPlatform();
  return cachedPlatform;
}

// ── Mobile breakpoint via useSyncExternalStore ─────────────────────────
const mobileQuery =
  typeof window !== 'undefined'
    ? window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`)
    : null;

function subscribeMobile(callback: () => void) {
  mobileQuery?.addEventListener('change', callback);
  return () => mobileQuery?.removeEventListener('change', callback);
}

function getMobileSnapshot() {
  return mobileQuery?.matches ?? false;
}

function getMobileServerSnapshot() {
  return false;
}

// ── Keyboard & viewport via useSyncExternalStore ───────────────────────
interface ViewportState {
  isKeyboardOpen: boolean;
  viewportHeight: number;
}

let viewportState: ViewportState = { isKeyboardOpen: false, viewportHeight: 0 };
let initialViewportHeight = 0;

function subscribeViewport(callback: () => void) {
  const vv = window.visualViewport;
  if (!vv) {
    viewportState = { isKeyboardOpen: false, viewportHeight: window.innerHeight };
    return () => {};
  }

  initialViewportHeight = vv.height;
  viewportState = { isKeyboardOpen: false, viewportHeight: vv.height };

  const onResize = () => {
    const currentHeight = vv.height;
    const heightDropped = currentHeight < initialViewportHeight * 0.75;
    const activeEl = document.activeElement;
    const inputFocused = activeEl !== null && INPUT_TAGS.has(activeEl.tagName);

    viewportState = {
      isKeyboardOpen: heightDropped && inputFocused,
      viewportHeight: currentHeight,
    };
    callback();
  };

  vv.addEventListener('resize', onResize);
  return () => vv.removeEventListener('resize', onResize);
}

function getViewportSnapshot(): ViewportState {
  return viewportState;
}

const serverViewportState: ViewportState = { isKeyboardOpen: false, viewportHeight: 0 };
function getViewportServerSnapshot(): ViewportState {
  return serverViewportState;
}

// ── Hook ───────────────────────────────────────────────────────────────
export interface DeviceCapabilities {
  isMobile: boolean;
  isIOS: boolean;
  isAndroid: boolean;
  isKeyboardOpen: boolean;
  viewportHeight: number;
}

export function useDeviceCapabilities(): DeviceCapabilities {
  const isMobile = useSyncExternalStore(
    subscribeMobile,
    getMobileSnapshot,
    getMobileServerSnapshot,
  );
  const viewport = useSyncExternalStore(
    subscribeViewport,
    getViewportSnapshot,
    getViewportServerSnapshot,
  );
  const platform = getPlatform();

  return {
    isMobile,
    isIOS: platform.isIOS,
    isAndroid: platform.isAndroid,
    ...viewport,
  };
}
